import React, { useState, useEffect } from 'react';
import { ChevronRight, Check, AlertCircle, Send, Globe, User, Calendar, Palette, FileText, MessageSquare, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { fragebogenService } from '../services/fragebogenService';
import { quoteService } from '../services/quoteService';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export default function WebsiteFragebogen() {
  const { user, userProfile } = useAuth();
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    projektName: '',
    kontaktName: '',
    kontaktEmail: '',
    domain: '',
    websiteZiele: [] as string[],
    andereZiele: '',
    zielgruppe: '',
    seiten: [] as string[],
    sonstigeSeite: '',
    hasLogo: null as null | string,
    hasFarben: null as null | string,
    referenzSeiten: '',
    referenzLink1: '',
    referenzLink2: '',
    texteVorhanden: '',
    anzahlBilder: '',
    stockFotos: null as null | string,
    kontaktformular: null as null | string,
    socialMedia: null as null | string,
    newsletter: null as null | string,
    zeitplan: ''
  });

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Get quote ID from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('quoteId');
    if (id) {
      setQuoteId(id);
      // Optionally pre-fill form data from quote
      loadQuoteData(id);
    }
  }, []);
  
  const loadQuoteData = async (id: string) => {
    try {
      const quote = await quoteService.getQuote(id);
      if (quote && user) {
        // Pre-fill contact info from user profile
        setFormData(prev => ({
          ...prev,
          kontaktName: quote.clientName || userProfile?.displayName || '',
          kontaktEmail: quote.clientEmail || user.email || ''
        }));
      }
    } catch (error) {
      console.error('Error loading quote:', error);
    }
  };

  const websiteZieleOptionen = [
    'Dein Projekt / Unternehmen vorstellen',
    'Eine Veranstaltung bewerben',
    'Deine Leistungen / dein Portfolio zeigen',
    'Tickets verkaufen',
    'Community aufbauen'
  ];

  const seitenOptionen = [
    'Startseite',
    'Über uns / Unsere Geschichte',
    'Leistungen / Angebot',
    'Produkte / Portfolio',
    'Events / Termine',
    'Kontakt'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      if (name === 'websiteZiele') {
        setFormData(prev => ({
          ...prev,
          websiteZiele: checked 
            ? [...prev.websiteZiele, value]
            : prev.websiteZiele.filter(item => item !== value)
        }));
      } else if (name === 'seiten') {
        if (checked && formData.seiten.length >= 3) {
          alert('Hey! Im Basis-Paket sind max. 3 Seiten enthalten. Wenn du mehr brauchst, erwähne das bitte im Kommentarfeld am Ende 😊');
          return;
        }
        setFormData(prev => ({
          ...prev,
          seiten: checked
            ? [...prev.seiten, value]
            : prev.seiten.filter(item => item !== value)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};
    
    if (step === 1) {
      if (!formData.projektName) newErrors.projektName = 'Bitte gib deinem Projekt einen Namen';
      if (!formData.kontaktName) newErrors.kontaktName = 'Wir brauchen einen Ansprechpartner';
      if (!formData.kontaktEmail) newErrors.kontaktEmail = 'E-Mail fehlt noch';
      if (formData.kontaktEmail && !/\S+@\S+\.\S+/.test(formData.kontaktEmail)) {
        newErrors.kontaktEmail = 'Check mal die E-Mail-Adresse';
      }
    }
    
    if (step === 2) {
      if (formData.websiteZiele.length === 0 && !formData.andereZiele) {
        newErrors.websiteZiele = 'Sag uns, was du erreichen willst';
      }
      if (!formData.zielgruppe) newErrors.zielgruppe = 'Wer soll deine Seite besuchen?';
    }
    
    if (step === 3) {
      if (formData.seiten.length === 0) {
        newErrors.seiten = 'Wähle mindestens eine Seite aus';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 7));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!user || !userProfile) {
      alert('Sie müssen angemeldet sein, um das Formular einzureichen.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Save fragebogen to Firestore
      const fragebogenData = {
        ...formData,
        quoteId: quoteId || undefined,
        submittedByUserId: user.uid,
        submittedByUserName: userProfile.displayName
      };
      
      const fragebogenId = await fragebogenService.createFragebogen(fragebogenData);
      console.log('✅ Fragebogen saved:', fragebogenId);
      
      // Update linked quote if exists
      if (quoteId) {
        const quoteRef = doc(db, 'quotes', quoteId);
        await updateDoc(quoteRef, {
          fragebogenSubmitted: true,
          fragebogenSubmittedAt: new Date(),
          fragebogenId: fragebogenId
        });
        console.log('✅ Quote updated with fragebogen submission');
      }
      
      // Show success state instead of mailto
      setIsSubmitted(true);
      
    } catch (error) {
      console.error('❌ Error submitting fragebogen:', error);
      alert('Fehler beim Speichern des Fragebogens. Bitte versuchen Sie es erneut.');
      setIsSubmitting(false);
    }
  };

  // exportAsJSON helper removed because it's currently unused

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-2">
        {[1, 2, 3, 4, 5, 6, 7].map((step) => (
          <div key={step} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
              ${currentStep === step ? 'bg-blue-600 text-white' : 
                currentStep > step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
              {currentStep > step ? <Check className="w-4 h-4" /> : step}
            </div>
            {step < 7 && (
              <div className={`w-8 h-0.5 ${currentStep > step ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Show success message if submitted
  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 md:p-12">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-25"></div>
              <div className="relative bg-green-500 rounded-full p-4">
                <CheckCircle className="w-16 h-16 text-white" strokeWidth={2.5} />
              </div>
            </div>
          </div>

          {/* Success Message */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Fast geschafft! 🎉
            </h1>
            <p className="text-lg text-gray-700 mb-2">
              Danke für deine Angaben!
            </p>
            <p className="text-gray-600">
              Wir haben alle Infos erhalten und melden uns innerhalb von 24h bei dir.
              Jetzt können wir direkt loslegen! 🚀
            </p>
          </div>

          {/* CTA */}
          <button
            onClick={() => window.location.href = '/my-orders'}
            className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl font-bold text-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
          >
            Zu meinen Bestellungen
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Du hast Fragen? Schreib uns einfach an{' '}
              <a href="mailto:emanuel@chimperator-live.de" className="text-blue-600 hover:underline">
                emanuel@chimperator-live.de
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Info banner - Quote linked or not */}
          {quoteId ? (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-900">
                ✓ Dieser Fragebogen ist mit Ihrem akzeptierten Angebot verknüpft.
              </p>
            </div>
          ) : userProfile?.role !== 'admin' && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Hinweis:</strong> Normalerweise sollten Sie diesen Fragebogen nach Akzeptanz
                Ihres Angebots ausfüllen. Wenn Sie hierher weitergeleitet wurden, können Sie trotzdem
                fortfahren.
              </p>
            </div>
          )}
          
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Hey! Lass uns deine Website planen 🚀
            </h1>
            <p className="text-gray-600">
              Nimm dir 5 Minuten Zeit und beantworte uns ein paar Fragen
            </p>
          </div>

          <StepIndicator />

          {/* Step 1: Über dich */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <User className="w-5 h-5 mr-2 text-blue-600" />
                Über dich
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wie heißt dein Projekt / deine Firma? *
                </label>
                <input
                  type="text"
                  name="projektName"
                  value={formData.projektName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${errors.projektName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="z.B. Cronicles Open Air"
                />
                {errors.projektName && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.projektName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wer ist unser Hauptkontakt? (Name) *
                </label>
                <input
                  type="text"
                  name="kontaktName"
                  value={formData.kontaktName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${errors.kontaktName ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Dein Name"
                />
                {errors.kontaktName && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.kontaktName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail *
                </label>
                <input
                  type="email"
                  name="kontaktEmail"
                  value={formData.kontaktEmail}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${errors.kontaktEmail ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="deine@email.de"
                />
                {errors.kontaktEmail && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.kontaktEmail}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gibt's schon eine Domain?
                </label>
                <input
                  type="text"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="www.deine-domain.de (optional)"
                />
              </div>
            </div>
          )}

          {/* Step 2: Ziel der Website */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Globe className="w-5 h-5 mr-2 text-blue-600" />
                Ziel der Website
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Was willst du mit der Website erreichen? *
                </label>
                <div className="space-y-2">
                  {websiteZieleOptionen.map((option) => (
                    <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        name="websiteZiele"
                        value={option}
                        checked={formData.websiteZiele.includes(option)}
                        onChange={handleInputChange}
                        className="mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
                <input
                  type="text"
                  name="andereZiele"
                  value={formData.andereZiele}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 mt-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Etwas anderes..."
                />
                {errors.websiteZiele && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.websiteZiele}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wer soll deine Seite besuchen? *
                </label>
                <textarea
                  name="zielgruppe"
                  value={formData.zielgruppe}
                  onChange={handleInputChange}
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${errors.zielgruppe ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="z.B. junge Leute aus Stuttgart, die feiern gehen wollen"
                />
                {errors.zielgruppe && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.zielgruppe}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Seitenaufbau */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Seitenaufbau (max. 3 Seiten)
              </h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  👉 <strong>Hinweis:</strong> Im Basis-Paket sind <strong>max. 3 Seiten</strong> enthalten. 
                  Impressum & Datenschutz kommen automatisch dazu.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Bitte wähle bis zu 3 Hauptseiten aus: *
                </label>
                <div className="space-y-2">
                  {seitenOptionen.map((option) => (
                    <label key={option} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        name="seiten"
                        value={option}
                        checked={formData.seiten.includes(option)}
                        onChange={handleInputChange}
                        disabled={!formData.seiten.includes(option) && formData.seiten.length >= 3}
                        className="mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <span className={`${!formData.seiten.includes(option) && formData.seiten.length >= 3 ? 'text-gray-400' : 'text-gray-700'}`}>
                        {option}
                        {option === 'Startseite' && <span className="text-sm text-gray-500 ml-2">(empfohlen)</span>}
                      </span>
                    </label>
                  ))}
                  {/* Sonstige as selectable option */}
                  <div className="p-3 border border-gray-200 rounded-lg">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="seiten"
                        value="Sonstige"
                        checked={formData.seiten.includes('Sonstige')}
                        onChange={handleInputChange}
                        disabled={!formData.seiten.includes('Sonstige') && formData.seiten.length >= 3}
                        className="mr-3 text-blue-600 focus:ring-blue-500"
                      />
                      <span className={`${!formData.seiten.includes('Sonstige') && formData.seiten.length >= 3 ? 'text-gray-400' : 'text-gray-700'}`}>
                        Sonstige
                      </span>
                    </label>
                    {formData.seiten.includes('Sonstige') && (
                      <input
                        type="text"
                        name="sonstigeSeite"
                        value={formData.sonstigeSeite}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 mt-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        placeholder="Beschreibe die zusätzliche Seite..."
                      />
                    )}
                  </div>
                </div>
                {errors.seiten && (
                  <p className="text-red-500 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.seiten}
                  </p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <strong>Ausgewählt:</strong> {formData.seiten.length} von 3 Seiten
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Look & Feel */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Palette className="w-5 h-5 mr-2 text-blue-600" />
                Look & Feel
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Hast du schon ein Logo?
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasLogo"
                      value="ja"
                      checked={formData.hasLogo === 'ja'}
                      onChange={(e) => setFormData({...formData, hasLogo: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Ja</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasLogo"
                      value="nein"
                      checked={formData.hasLogo === 'nein'}
                      onChange={(e) => setFormData({...formData, hasLogo: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Nein</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Hast du bestimmte Farben oder Schriften, die du nutzen willst?
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasFarben"
                      value="ja"
                      checked={formData.hasFarben === 'ja'}
                      onChange={(e) => setFormData({...formData, hasFarben: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Ja</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="hasFarben"
                      value="nein"
                      checked={formData.hasFarben === 'nein'}
                      onChange={(e) => setFormData({...formData, hasFarben: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Nein</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kennst du 1-2 Webseiten, die dir vom Stil gefallen?
                </label>
                <textarea
                  name="referenzSeiten"
                  value={formData.referenzSeiten}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Bitte Links eintragen (optional)"
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Link 1 (optional)</label>
                    <input
                      type="url"
                      name="referenzLink1"
                      value={formData.referenzLink1}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Link 2 (optional)</label>
                    <input
                      type="url"
                      name="referenzLink2"
                      value={formData.referenzLink2}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Inhalte */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Inhalte
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Welche Texte kannst du liefern?
                </label>
                <textarea
                  name="texteVorhanden"
                  value={formData.texteVorhanden}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="z.B. kurze Beschreibung über dich / dein Projekt, Leistungen, Kontaktinfos"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bilder - Wie viele Fotos kannst du ungefähr liefern?
                </label>
                <input
                  type="text"
                  name="anzahlBilder"
                  value={formData.anzahlBilder}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="z.B. 10-15 Fotos"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Brauchst du zusätzlich Stock-Fotos (lizenzfreie Platzhalter)?
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="stockFotos"
                      value="ja"
                      checked={formData.stockFotos === 'ja'}
                      onChange={(e) => setFormData({...formData, stockFotos: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Ja</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="stockFotos"
                      value="nein"
                      checked={formData.stockFotos === 'nein'}
                      onChange={(e) => setFormData({...formData, stockFotos: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Nein</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Extras */}
          {currentStep === 6 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-blue-600" />
                Extras
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Möchtest du ein Kontaktformular?
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="kontaktformular"
                      value="ja"
                      checked={formData.kontaktformular === 'ja'}
                      onChange={(e) => setFormData({...formData, kontaktformular: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Ja</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="kontaktformular"
                      value="nein"
                      checked={formData.kontaktformular === 'nein'}
                      onChange={(e) => setFormData({...formData, kontaktformular: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Nein</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Social Media Links (Instagram, Facebook etc.)?
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="socialMedia"
                      value="ja"
                      checked={formData.socialMedia === 'ja'}
                      onChange={(e) => setFormData({...formData, socialMedia: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Ja</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="socialMedia"
                      value="nein"
                      checked={formData.socialMedia === 'nein'}
                      onChange={(e) => setFormData({...formData, socialMedia: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Nein</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Newsletter-Anmeldung?
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="newsletter"
                      value="ja"
                      checked={formData.newsletter === 'ja'}
                      onChange={(e) => setFormData({...formData, newsletter: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Ja</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="newsletter"
                      value="nein"
                      checked={formData.newsletter === 'nein'}
                      onChange={(e) => setFormData({...formData, newsletter: e.target.value})}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Nein</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 7: Zeitplan */}
          {currentStep === 7 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                Zeitplan
              </h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gibt's einen Termin oder ein Event, bis wann die Seite online sein sollte?
                </label>
                <textarea
                  name="zeitplan"
                  value={formData.zeitplan}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="z.B. Bis zum 1. März wegen Eventstart"
                />
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                  Fast geschafft! 🎉
                </h3>
                <p className="text-sm text-green-700 mb-4">
                  Danke für deine Angaben. Nach dem Absenden melden wir uns innerhalb von 24h bei dir.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {isSubmitting ? 'Wird gespeichert...' : 'Anfrage abschicken'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            {currentStep > 1 && (
              <button
                onClick={prevStep}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Zurück
              </button>
            )}
            {currentStep < 7 && (
              <button
                onClick={nextStep}
                className="ml-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                Weiter
                <ChevronRight className="w-4 h-4 ml-2" />
              </button>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-white rounded-xl shadow-md p-6">
          <h3 className="font-semibold text-gray-800 mb-2">💡 Gut zu wissen:</h3>
          <ul className="space-y-1 text-sm text-gray-600">
            <li>• Basis-Paket enthält max. 3 Seiten + Impressum/Datenschutz</li>
            <li>• Du kannst alle Inhalte später noch ändern</li>
            <li>• Wir verwenden moderne Templates, die auf allen Geräten super aussehen</li>
            <li>• Bei Fragen einfach melden - wir helfen gerne!</li>
          </ul>
        </div>
      </div>
    </div>
  );
}


